import { gsap } from '../gsap-config.js';
import { initAnimationsForElement, cleanupAnimationsForElement } from '../utils/animations.js';
import { prefersReducedMotion } from '../utils/media.js';

const PANEL_EASE = 'power2.inOut';
const PANEL_DURATION = 0.48;
const INNER_EASE = 'power2.out';
const INNER_DURATION = 0.36;

class Accordion extends HTMLElement {
	#abort;
	#ctx;
	#isAnimating = false;

	connectedCallback() {
		this.#abort = new AbortController();
		this.#ctx = gsap.context(() => {}, this);
		this.#init();

		requestAnimationFrame(() => {
			initAnimationsForElement(this);
		});
	}

	disconnectedCallback() {
		this.#abort?.abort();
		this.#ctx?.revert();
		cleanupAnimationsForElement(this);
	}

	#getInner(content) {
		return content.querySelector('.tt-accordion-content-inner') ?? content;
	}

	#init() {
		if (!this.classList.contains('tt-accordion')) {
			this.classList.add('tt-accordion');
		}

		const opts = { signal: this.#abort.signal };

		this.querySelectorAll('.tt-accordion-item').forEach((item, index) => {
			const heading = item.querySelector('.tt-accordion-heading');
			const content = item.querySelector('.tt-accordion-content');

			if (!heading || !content) return;

			const contentId = content.id || `accordion-content-${index}`;
			content.id = contentId;
			heading.setAttribute('aria-controls', contentId);

			const inner = this.#getInner(content);

			if (item.classList.contains('active')) {
				heading.setAttribute('aria-expanded', 'true');
				content.classList.add('is-open');
				content.style.removeProperty('display');
				gsap.set(content, { height: 'auto', overflow: 'hidden' });
				gsap.set(inner, { opacity: 1, y: 0 });
			} else {
				heading.setAttribute('aria-expanded', 'false');
				content.classList.remove('is-open');
				content.style.removeProperty('display');
				gsap.set(content, { height: 0, overflow: 'hidden' });
				gsap.set(inner, { opacity: 0, y: 6 });
			}
		});

		this.querySelectorAll('.tt-accordion-heading').forEach((heading) => {
			heading.addEventListener('click', (e) => {
				e.preventDefault();
				this.#toggleItem(heading);
			}, opts);

			heading.addEventListener('keydown', (e) => {
				if (e.key === 'Enter' || e.key === ' ') {
					e.preventDefault();
					this.#toggleItem(heading);
				}
			}, opts);
		});
	}

	#measureContentHeight(content) {
		gsap.set(content, { height: 'auto', overflow: 'hidden' });
		const height = content.offsetHeight;
		gsap.set(content, { height: 0 });
		return height;
	}

	#toggleItem(heading) {
		if (this.#isAnimating) return;

		const item = heading.closest('.tt-accordion-item');
		const content = item?.querySelector('.tt-accordion-content');
		const inner = this.#getInner(content);

		if (!item || !content) return;

		if (item.classList.contains('active')) {
			this.#closeItem(item, heading, content, inner);
			return;
		}

		const openOthers = this.querySelectorAll('.tt-accordion-item.active');
		openOthers.forEach((otherItem) => {
			if (otherItem === item) return;
			const otherHeading = otherItem.querySelector('.tt-accordion-heading');
			const otherContent = otherItem.querySelector('.tt-accordion-content');
			const otherInner = otherContent ? this.#getInner(otherContent) : null;
			if (otherHeading && otherContent && otherInner) {
				this.#closeItem(otherItem, otherHeading, otherContent, otherInner, false);
			}
		});

		this.#openItem(item, heading, content, inner);
	}

	#openItem(item, heading, content, inner) {
		if (prefersReducedMotion()) {
			item.classList.add('active');
			content.classList.add('is-open');
			heading.setAttribute('aria-expanded', 'true');
			gsap.set(content, { height: 'auto', overflow: 'hidden', clearProps: 'opacity' });
			gsap.set(inner, { opacity: 1, y: 0, clearProps: 'transform,opacity' });
			return;
		}

		this.#isAnimating = true;
		item.classList.add('active');
		heading.setAttribute('aria-expanded', 'true');
		content.classList.add('is-open');

		const targetHeight = this.#measureContentHeight(content);

		gsap.killTweensOf([content, inner]);

		this.#ctx.add(() => {
			const tl = gsap.timeline({
				onComplete: () => {
					gsap.set(content, { height: 'auto' });
					this.#isAnimating = false;
				},
			});

			tl.to(content, {
				height: targetHeight,
				duration: PANEL_DURATION,
				ease: PANEL_EASE,
			}, 0);

			tl.fromTo(inner, {
				opacity: 0,
				y: 10,
			}, {
				opacity: 1,
				y: 0,
				duration: INNER_DURATION,
				ease: INNER_EASE,
			}, 0.08);
		});
	}

	#closeItem(item, heading, content, inner, trackLock = true) {
		if (!item.classList.contains('active')) return;

		if (prefersReducedMotion()) {
			item.classList.remove('active');
			content.classList.remove('is-open');
			heading.setAttribute('aria-expanded', 'false');
			gsap.set(content, { height: 0, overflow: 'hidden' });
			gsap.set(inner, { opacity: 0, y: 6 });
			return;
		}

		if (trackLock) this.#isAnimating = true;

		heading.setAttribute('aria-expanded', 'false');
		const startHeight = content.offsetHeight;

		gsap.killTweensOf([content, inner]);
		gsap.set(content, { height: startHeight, overflow: 'hidden' });

		this.#ctx.add(() => {
			const tl = gsap.timeline({
				onComplete: () => {
					item.classList.remove('active');
					content.classList.remove('is-open');
					gsap.set(content, { height: 0, overflow: 'hidden' });
					if (trackLock) this.#isAnimating = false;
				},
			});

			tl.to(inner, {
				opacity: 0,
				y: 6,
				duration: INNER_DURATION * 0.75,
				ease: 'power2.in',
			}, 0);

			tl.to(content, {
				height: 0,
				duration: PANEL_DURATION,
				ease: PANEL_EASE,
			}, 0);
		});
	}
}

customElements.define('tt-accordion', Accordion);
